/**
 * ArticleCard component
 *
 * This component handles all the different article card layouts and sizes.
 * It is responsible for rendering the article card based on the provided 'cardType' prop.
 *
 * The naming conventions for the card sizes and layouts are as follows:
 *
 * There are 5 different featured article types (Figma - Articles tiles 1/1, alternatives 1-5),
 * their modifiers are from --1 to --5 (same order as in the Design System).
 *
 * Non-featured article tiles (Figma - article tiles basic):
 * - 1/3: --one-third
 * - 1/2: --half
 * - 2/3: --two-third
 * - 1/2 horizontal: --small
 *
 * Each variant corresponds to a different layout and size for the article card.
 */
import React, { FunctionComponent, useState } from 'react';
import he from 'he';
import { Article } from '../types/types';
import { PrintArticle } from '../../print/types/PrintArticle';
import ArticleMeta from '../../components/article-meta/ArticleMeta';
import Tag from '../../components/tag/Tag';

// Define a union type for the `article` prop
interface ArticleCardProps {
  article: Article | PrintArticle;
  cardType: 1 | 2 | 3 | 4 | 5 | 'one-third' | 'half' | 'two-third' | 'small' | undefined;
}

const ArticleCard: FunctionComponent<
  ArticleCardProps & {
    openDialog?: () => void;
    removeArticle?: (id: string | undefined) => void;
  }
> = ({ article, cardType, openDialog = () => {}, removeArticle = () => {} }) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const { id, title, authorName, tagLabel, publishDate, readTime, link, categoryLink, tagIcon, authorLink, perex } =
    article;

  // Determine the correct image property, fallback to a default if necessary
  const imageLink = article?.imageLink;

  const renderCardClassName = () => {
    switch (cardType) {
      case 1:
        return 'react-block-article-card--1';
      case 2:
        return 'react-block-article-card--2';
      case 3:
        return 'react-block-article-card--3';
      case 4:
        return 'react-block-article-card--4';
      case 5:
        return 'react-block-article-card--5';
      case 'two-third':
        return 'react-block-article-card--two-third';
      case 'half':
        return 'react-block-article-card--half';
      case 'one-third':
        return 'react-block-article-card--one-third';
      case 'small':
        return 'react-block-article-card--small';
      default:
        return '';
    }
  };

  const decodedTitle = he.decode(title ?? '');

  const renderCardHeading = () => {
    switch (cardType) {
      case 5:
        return <h3 className="react-block-article-card__title h3-noto">{decodedTitle}</h3>;
      case 4:
      case 3:
      case 'two-third':
        return <h4 className="react-block-article-card__title h4-noto">{decodedTitle}</h4>;
      case 'half':
        return <h5 className="react-block-article-card__title h5-noto">{decodedTitle}</h5>;
      case 'one-third':
      case 'small':
        return <h6 className="react-block-article-card__title h6-noto">{decodedTitle}</h6>;
      default:
        return <h6 className="react-block-article-card__title h6-noto">{decodedTitle}</h6>;
    }
  };

  return (
    <div className={`react-block-article-card ${renderCardClassName()}`}>
      <div
        className="react-block-article-card__image-wrapper"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <a href={link} className="react-block-article-card__image">
          <img loading="lazy" src={imageLink} alt={title} />
        </a>

        {tagLabel && (
          <div className="react-block-article-card__tag-wrapper react-block-article-card__tag-wrapper--image">
            <Tag text={tagLabel} url={categoryLink} icon={tagIcon ? tagIcon : undefined} hasBackground={true} />
          </div>
        )}
      </div>

      <div className="react-block-article-card__content-wrapper">
        <div
          className="react-block-article-card__title-wrapper"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {tagLabel && (
            <div className="react-block-article-card__tag-wrapper react-block-article-card__tag-wrapper--content">
              <Tag text={tagLabel} url={categoryLink} icon={tagIcon ? tagIcon : undefined} hasBackground={true} />
            </div>
          )}

          <a
            href={link}
            className={`react-block-article-card__title-link ${isHovered ? 'hovered' : ''}`}
            rel="noopener noreferrer"
          >
            {renderCardHeading()}
          </a>
        </div>

        {perex && perex?.length > 0 && cardType !== 'small' && (
          <a
            href={link}
            className={`desktop-body article-excerpt ${isHovered ? 'hovered' : ''}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {perex}
          </a>
        )}

        <ArticleMeta
          removeArticle={removeArticle}
          articleAuthors={[{ name: authorName, link: authorLink }]}
          articleData={{
            id: id,
            title: title,
            url: link,
            image: imageLink,
            publishDate: publishDate,
            readTime: readTime,
          }}
        />
      </div>
    </div>
  );
};

export default ArticleCard;
